import { useEffect } from "react";
import { BrandMark } from "../BrandMark";
import { brandDirectory, type BrandDetail } from "../../data/brandDirectory";
import { useLocale } from "../../i18n/LocaleContext";
import { buildLocalizedPath } from "../../lib/siteRouting";

type Props = {
  brand: BrandDetail;
};

export function BrandDetailPage({ brand }: Props) {
  const { locale } = useLocale();
  const isChinese = locale === "zh" || locale === "vi";
  const usesPlaceholderVisual = brand.heroImage.includes("/brand-placeholders/");
  const isReferenceBrand = usesPlaceholderVisual && !brand.officialUrl;
  const usesContainedHero =
    usesPlaceholderVisual ||
    brand.heroImage.includes("-logo") ||
    brand.heroImage.includes("/yomei-materials/") ||
    brand.heroImage.includes("/mihwanghoo-materials/");
  const detailPath = buildLocalizedPath(locale, `/brands/${brand.slug}`);
  const galleryImages = (brand.galleryImages?.length
    ? brand.galleryImages
    : [
        { src: brand.heroImage, label: brand.displayName ?? brand.name, fit: brand.heroFit },
      ]).filter((image) => !image.src.includes("/brand-placeholders/"));
  const relatedBrands = brandDirectory
    .filter((item) => item.slug !== brand.slug)
    .sort((a, b) => {
      const aScore =
        Number(a.category === brand.category) +
        Number(a.markets.some((market) => brand.markets.includes(market)));
      const bScore =
        Number(b.category === brand.category) +
        Number(b.markets.some((market) => brand.markets.includes(market)));
      return bScore - aScore;
    })
    .slice(0, 3);

  useEffect(() => {
    const pageTitle = `${brand.displayName ?? brand.name} | QIANDU`;
    const canonical = `https://www.qiandu.group${detailPath}`;
    const pageDescription = `${brand.summary} ${brand.relationship}`.trim();

    const setMeta = (selector: string, value: string, attr = "content") => {
      const node = document.head.querySelector(selector);
      if (node) {
        node.setAttribute(attr, value);
      }
    };

    document.title = pageTitle;
    setMeta('meta[property="og:title"]', pageTitle);
    setMeta('meta[name="twitter:title"]', pageTitle);
    setMeta('meta[name="description"]', pageDescription);
    setMeta('meta[property="og:description"]', pageDescription);
    setMeta('meta[name="twitter:description"]', pageDescription);
    setMeta('meta[property="og:url"]', canonical);

    const canonicalLink = document.head.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", canonical);
    }
  }, [brand.displayName, brand.name, brand.slug, brand.summary, brand.relationship, detailPath]);

  const labels = {
    en: {
      back: "Back to Home",
      eyebrow: "Brand Detail",
      overview: "Overview",
      snapshot: "Brand Snapshot",
      markets: "Markets",
      role: "Role",
      relationship: "Relationship",
      official: "Official",
      gallery: "Visual Gallery",
      reference: "Brand Reference",
      referenceNote: "Presented through a restrained brand-reference format while fuller public imagery remains limited.",
      related: "Related Brands",
      discover: "Discover Brand",
      relatedEyebrow: "Selected Match",
      finalCta: "Discuss this brand with QIANDU",
      finalBody: "For distribution, retail, or market-entry collaboration, contact the QIANDU team directly.",
      visit: "Visit Official Site",
      contact: "Request Partnership Details",
      visual: "Brand Visual",
    },
    zh: {
      back: "返回首页",
      eyebrow: "品牌详情",
      overview: "品牌介绍",
      snapshot: "品牌摘要",
      markets: "市场",
      role: "合作角色",
      relationship: "合作关系",
      official: "官网",
      gallery: "品牌视觉",
      reference: "品牌参考",
      referenceNote: "在公开完整视觉资料有限的情况下，以更克制的品牌参考方式呈现。",
      related: "相关品牌",
      discover: "查看品牌",
      relatedEyebrow: "相关品牌",
      finalCta: "与千渡沟通该品牌合作",
      finalBody: "如需了解代理、渠道、零售或市场进入合作，请直接联系千渡团队。",
      visit: "访问官网",
      contact: "咨询合作详情",
      visual: "品牌视觉",
    },
    ko: {
      back: "홈으로 돌아가기",
      eyebrow: "브랜드 상세",
      overview: "브랜드 소개",
      snapshot: "브랜드 스냅샷",
      markets: "시장",
      role: "역할",
      relationship: "협업 구조",
      official: "공식 사이트",
      gallery: "브랜드 비주얼",
      reference: "브랜드 레퍼런스",
      referenceNote: "공개 비주얼 자료가 제한적인 브랜드는 보다 절제된 레퍼런스 형식으로 소개합니다.",
      related: "연관 브랜드",
      discover: "브랜드 보기",
      relatedEyebrow: "연관 브랜드",
      finalCta: "QIANDU와 브랜드 협업 논의",
      finalBody: "유통, 리테일, 시장 진입 협업은 QIANDU 팀에 직접 문의해 주세요.",
      visit: "공식 사이트 보기",
      contact: "파트너십 문의",
      visual: "브랜드 비주얼",
    },
    vi: {
      back: "Quay lại trang chủ",
      eyebrow: "Chi tiết thương hiệu",
      overview: "Giới thiệu",
      snapshot: "Tóm tắt thương hiệu",
      markets: "Thị trường",
      role: "Vai trò",
      relationship: "Quan hệ hợp tác",
      official: "Website",
      gallery: "Hình ảnh thương hiệu",
      reference: "Tham chiếu thương hiệu",
      referenceNote: "Được trình bày theo định dạng tham chiếu tinh gọn khi tư liệu hình ảnh công khai còn hạn chế.",
      related: "Thương hiệu liên quan",
      discover: "Xem thương hiệu",
      relatedEyebrow: "Thương hiệu liên quan",
      finalCta: "Trao đổi thương hiệu này với QIANDU",
      finalBody: "Với nhu cầu phân phối, bán lẻ hoặc mở thị trường, hãy liên hệ trực tiếp đội ngũ QIANDU.",
      visit: "Xem website chính thức",
      contact: "Yêu cầu thông tin hợp tác",
      visual: "Hình ảnh thương hiệu",
    },
  }[locale];
  return (
    <main className="px-5 pb-20 pt-24 sm:px-8 lg:px-12 lg:pb-24 lg:pt-32">
      <div className="mx-auto max-w-[88rem]">
        <a
          href={buildLocalizedPath(locale, "/")}
          className="inline-flex rounded-full border border-[#dce8ee] bg-[rgba(251,254,255,0.82)] px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-soft/64 shadow-[0_10px_22px_rgba(106,138,151,0.06)] backdrop-blur"
        >
          {labels.back}
        </a>
        <div className="mt-8 grid gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:items-start">
          <div className="overflow-hidden rounded-[3rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.9),rgba(240,246,249,0.94))] p-4 shadow-[0_10px_20px_rgba(106,138,151,0.045)]">
            <div className="relative aspect-[1.12/0.98] overflow-hidden rounded-[2.15rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
              {usesPlaceholderVisual ? (
                <div className="flex h-full w-full items-center justify-center px-6 py-8 sm:px-10">
                  <div className="w-full max-w-[24rem]">
                    <div className="rounded-[2.1rem] border border-[#e1ebf0] bg-[linear-gradient(180deg,rgba(252,254,255,0.98),rgba(244,249,251,0.98))] px-6 py-8 shadow-[0_10px_22px_rgba(106,138,151,0.05)]">
                      <div className="text-center text-[7px] uppercase tracking-[0.42em] text-soft/42">
                        {labels.reference}
                      </div>
                      <div className="mt-5">
                        <BrandMark name={brand.logoName} />
                      </div>
                      <p className="mx-auto mt-5 max-w-[14rem] text-center text-[0.78rem] leading-6 text-soft/50">
                        {labels.referenceNote}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <img
                  src={brand.heroImage}
                  alt={brand.name}
                  className={`h-full w-full ${brand.heroFit === "contain" || usesContainedHero ? "object-contain bg-[rgba(246,249,250,0.94)] p-7 sm:p-10" : "object-cover object-center"}`}
                />
              )}
              {!usesPlaceholderVisual ? (
                <>
                  <div className="absolute left-5 top-5 rounded-full border border-white/28 bg-[rgba(255,255,255,0.2)] px-3 py-1.5 text-[7px] uppercase tracking-[0.36em] text-white/86 backdrop-blur">
                    {labels.visual}
                  </div>
                  <div className="absolute bottom-5 left-5 rounded-full border border-white/24 bg-[rgba(31,43,49,0.22)] px-3 py-1.5 text-[7px] uppercase tracking-[0.32em] text-white/82 backdrop-blur">
                    {brand.markets.slice(0, 2).join(" / ")}
                  </div>
                </>
              ) : null}
            </div>
          </div>

          <div>
            <div className="text-[8px] uppercase tracking-[0.56em] text-aurora/72">
              {isReferenceBrand ? labels.reference : labels.eyebrow}
            </div>
            <div className="mt-5 max-w-max rounded-[2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.92),rgba(241,247,250,0.94))] px-5 py-4 shadow-[0_8px_18px_rgba(106,138,151,0.04)]">
              <BrandMark name={brand.logoName} />
            </div>
            <h1 className={`mt-6 leading-[1] text-[#1f2b31] ${isChinese ? "font-sans text-[1.92rem] font-medium sm:text-[2.58rem]" : "font-display text-[2.18rem] sm:text-[2.82rem]"}`}>
              {brand.displayName ?? brand.name}
            </h1>
            <p className="mt-5 max-w-[20rem] text-[0.84rem] leading-7 text-soft/54">{brand.summary}</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {brand.highlights.slice(0, 2).map((item) => (
                <span
                  key={item}
                  className="rounded-full bg-[rgba(252,255,255,0.82)] px-3.5 py-2 text-[7px] uppercase tracking-[0.36em] text-soft/48 shadow-[0_6px_14px_rgba(106,138,151,0.03)]"
                >
                  {item}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {brand.officialUrl ? (
                <a
                  href={brand.officialUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full bg-[linear-gradient(135deg,#88aebb,#9fc8d2)] px-6 py-3.5 text-[9px] uppercase tracking-[0.36em] text-white shadow-[0_10px_20px_rgba(123,167,184,0.16)]"
                >
                  {labels.visit}
                </a>
              ) : null}
              <a
                href="mailto:service@qiandu.group?subject=QIANDU%20Brand%20Inquiry"
                className="rounded-full border border-white/44 bg-[rgba(252,255,255,0.78)] px-6 py-3.5 text-[9px] uppercase tracking-[0.36em] text-[#1f2b31] shadow-[0_8px_18px_rgba(106,138,151,0.04)]"
              >
                {labels.contact}
              </a>
            </div>
            {brand.officialLabel ? (
              <div className="mt-5 text-[8px] uppercase tracking-[0.34em] text-soft/38">
                <span>{labels.official}</span>
                <span className="ml-3 text-[#1f2b31]">{brand.officialLabel}</span>
              </div>
            ) : null}

            <div className="mt-9 rounded-[2.2rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.82),rgba(240,246,249,0.9))] px-5 py-6 shadow-[0_8px_18px_rgba(106,138,151,0.045)]">
              <div className="grid gap-5 sm:grid-cols-3">
                <div>
                  <div className="text-[7px] uppercase tracking-[0.4em] text-soft/42">{labels.markets}</div>
                  <div className="mt-3 text-[0.84rem] leading-7 text-[#1f2b31]">{brand.markets.join(" / ")}</div>
                </div>
                <div>
                  <div className="text-[7px] uppercase tracking-[0.4em] text-soft/42">{labels.role}</div>
                  <div className="mt-3 text-[0.84rem] leading-7 text-[#1f2b31]">{brand.category}</div>
                </div>
                <div>
                  <div className="text-[7px] uppercase tracking-[0.4em] text-soft/42">{labels.relationship}</div>
                  <div className="mt-3 text-[0.84rem] leading-7 text-[#1f2b31]">{brand.relationship}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-[1.06fr_0.94fr]">
          <section className="rounded-[2.45rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.88),rgba(240,246,249,0.92))] p-6 shadow-[0_10px_20px_rgba(106,138,151,0.045)]">
            <div className="text-[7px] uppercase tracking-[0.4em] text-soft/42">
              {isReferenceBrand ? labels.reference : labels.overview}
            </div>
            <p className="mt-4 max-w-[22rem] text-[0.84rem] leading-7 text-soft/56">{brand.overview}</p>
          </section>

          <section className="rounded-[2.45rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.88),rgba(240,246,249,0.92))] p-6 shadow-[0_10px_20px_rgba(106,138,151,0.045)]">
            <div className="text-[7px] uppercase tracking-[0.4em] text-soft/42">{labels.snapshot}</div>
            <div className="mt-5 grid gap-3">
              {brand.highlights.slice(0, 3).map((item) => (
                <div
                  key={item}
                  className="rounded-[1.85rem] bg-[rgba(252,255,255,0.8)] px-4 py-4 text-[0.82rem] leading-7 text-[#1f2b31] shadow-[0_5px_12px_rgba(106,138,151,0.035)]"
                >
                  {item}
                </div>
              ))}
            </div>
          </section>
        </div>

        {galleryImages.length > 1 ? (
          <section className="mt-12 rounded-[2.25rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.88),rgba(240,246,249,0.92))] p-6 shadow-[0_12px_24px_rgba(106,138,151,0.06)]">
            <div className="text-[8px] uppercase tracking-[0.36em] text-soft/44">{labels.gallery}</div>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              {galleryImages.slice(0, 3).map((image) => (
                <div
                  key={`${image.src}-${image.label}`}
                  className="overflow-hidden rounded-[1.8rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))] shadow-[0_10px_22px_rgba(106,138,151,0.05)]"
                >
                  <div className="aspect-[1.08/0.9]">
                    <img
                      src={image.src}
                      alt={image.label}
                      className={`h-full w-full ${image.fit === "contain" ? "object-contain p-6" : "object-cover object-center"}`}
                    />
                  </div>
                  <div className="border-t border-[#dde8ee] px-4 py-3 text-[8px] uppercase tracking-[0.3em] text-soft/40">
                    {image.label}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="mt-12 rounded-[2.25rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.88),rgba(240,246,249,0.92))] p-6 shadow-[0_12px_24px_rgba(106,138,151,0.06)]">
          <div className="text-[8px] uppercase tracking-[0.36em] text-soft/44">{labels.related}</div>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {relatedBrands.map((item) => (
              (() => {
                const relatedIsReference = item.heroImage.includes("/brand-placeholders/");
                return (
              <a
                key={item.slug}
                href={buildLocalizedPath(locale, `/brands/${item.slug}`)}
                className="group rounded-[2rem] bg-[rgba(252,255,255,0.82)] p-4 shadow-[0_8px_18px_rgba(106,138,151,0.04)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_22px_rgba(106,138,151,0.06)]"
              >
                <div className="overflow-hidden rounded-[1.5rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
                  <div className="aspect-[1.16/0.92]">
                    {item.heroImage.includes("/brand-placeholders/") ? (
                      <div className="flex h-full w-full items-center justify-center px-4 py-5">
                        <div className="w-full max-w-[10rem]">
                          <BrandMark name={item.logoName} />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={item.heroImage}
                        alt={item.name}
                        className={`h-full w-full ${
                          item.heroFit === "contain" ||
                          item.heroImage.includes("-logo") ||
                          item.heroImage.includes("/yomei-materials/") ||
                          item.heroImage.includes("/mihwanghoo-materials/")
                              ? "object-contain p-5"
                            : "object-cover object-center"
                        }`}
                      />
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <BrandMark name={item.logoName} />
                </div>
                <div className="mt-4 text-[8px] uppercase tracking-[0.32em] text-soft/38">
                  {relatedIsReference ? labels.reference : labels.relatedEyebrow}
                </div>
                <p className="mt-4 line-clamp-2 text-[0.84rem] leading-7 text-soft/56">{item.summary}</p>
                <div className="mt-4 text-[8px] uppercase tracking-[0.32em] text-soft/40 transition group-hover:text-[#7fa7b4]">{labels.discover}</div>
              </a>
                );
              })()
            ))}
          </div>
        </section>

        <section className="mt-12 overflow-hidden rounded-[2.6rem] border border-[#dce8ee] bg-[linear-gradient(135deg,rgba(236,245,248,0.96),rgba(223,235,240,0.96))] p-7 text-[#223036] shadow-[0_16px_32px_rgba(106,138,151,0.08)] sm:p-9">
          <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <div className="text-[8px] uppercase tracking-[0.38em] text-soft/40">QIANDU / {brand.displayName ?? brand.name}</div>
              <h2 className="mt-4 font-display text-[1.82rem] leading-tight sm:text-[2.28rem]">{labels.finalCta}</h2>
              <p className="mt-4 max-w-[19rem] text-[0.84rem] leading-7 text-soft/56">{labels.finalBody}</p>
            </div>
            <a
              href={`mailto:service@qiandu.group?subject=QIANDU%20${encodeURIComponent(brand.name)}%20Inquiry`}
              className="inline-flex justify-center rounded-full bg-[linear-gradient(135deg,#88aebb,#9fc8d2)] px-6 py-3.5 text-[10px] uppercase tracking-[0.3em] text-white shadow-[0_10px_20px_rgba(123,167,184,0.18)] transition hover:-translate-y-0.5"
            >
              service@qiandu.group
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}
